import axios from 'axios';
import swal from "sweetalert";
import {
    logoutUser,
    checkAuthStatus,
} from '../store/actions/AuthActions';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance for auth requests
const authApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request logging for debugging
authApi.interceptors.request.use((config) => {
  console.log(`[AuthService] Request: ${config.method?.toUpperCase()} ${config.url}`);
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  console.error('[AuthService] Request error:', error);
  return Promise.reject(error);
});

// Add response error handling
authApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('[AuthService] Response error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method
    });
    return Promise.reject(error);
  }
);

export function signUp(userData) {
  return authApi.post('/auth/register', userData);
}

export function getVerificationConfig() {
  return authApi.get('/auth/verification-config');
}

export function sendOTP(userData) {
  return authApi.post('/auth/send-otp', userData);
}

export function verifyOTPAndRegister(data) {
  return authApi.post('/auth/verify-otp-register', data);
}

export function resendOTP(data) {
  return authApi.post('/auth/resend-otp', data);
}

export function sendOTPForLogin(userId) {
  return authApi.post('/auth/send-otp-login', { userId });
}

export function verifyOTPAndLogin(data) {
  return authApi.post('/auth/verify-otp-login', data);
}

export function login(username, password) {
  return authApi.post('/auth/login', { username, password });
}

export function googleLogin(token) {
  return authApi.post('/auth/google', { token });
}

export function verifyEmail(token) {
  return authApi.get(`/auth/verify-email?token=${token}`);
}

export function resendVerification(email) {
  return authApi.post('/auth/resend-verification', { email });
}

export function getCurrentUser() {
  return authApi.get('/auth/me');
}

export function changePassword(currentPassword, newPassword) {
  return authApi.post('/auth/change-password', { currentPassword, newPassword });
}

export function formatError(errorResponse) {
  console.log('[AuthService] Formatting error:', errorResponse);
  
  // Handle network errors (no response from server)
  if (!errorResponse.response && errorResponse.request) {
    swal("Connection Error", "Unable to connect to the server. Please check your internet connection and try again.", "error");
    return;
  }
  
  // Handle backend error format (direct error object)
  if (errorResponse.error) {
    swal("Oops", errorResponse.error, "error");
    return;
  }
  
  // Handle old Firebase error format (for backwards compatibility)
  if (errorResponse.error && errorResponse.error.message) {
    switch (errorResponse.error.message) {
      case 'EMAIL_EXISTS':
        swal("Oops", "Email already exists", "error");
        break;
      case 'EMAIL_NOT_FOUND':
        swal("Oops", "Email not found", "error", { button: "Try Again!" });
        break;
      case 'INVALID_PASSWORD':
        swal("Oops", "Invalid Password", "error", { button: "Try Again!" });
        break;
      case 'USER_DISABLED':
        return 'User Disabled';
      default:
        return '';
    }
  }
  
  // Handle axios error with response
  if (errorResponse.response && errorResponse.response.data) {
    const status = errorResponse.response.status;
    const error = errorResponse.response.data.error || 'An error occurred';
    
    // Special handling for specific HTTP status codes
    if (status === 401) {
      swal("Authentication Failed", error, "error");
    } else if (status === 404) {
      swal("Not Found", "The requested service is not available. Please try again later.", "error");
    } else if (status === 500) {
      swal("Server Error", "An internal server error occurred. Please try again later.", "error");
    } else {
      swal("Oops", error, "error");
    }
    return;
  }
  
  swal("Oops", "An unexpected error occurred", "error");
}

export function saveTokenInLocalStorage(tokenDetails) {
  // Store the backend JWT token (ENHANCED: direct keys + JSON for compatibility)
  const expireDate = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours
  const userDetails = {
    token: tokenDetails.token,
    user: tokenDetails.user,
    expireDate: expireDate.toISOString()
  };
  
  // 🔥 FIXED: Store both formats for full compatibility
  localStorage.setItem('userDetails', JSON.stringify(userDetails));
  localStorage.setItem('token', tokenDetails.token); // Direct fallback
  localStorage.setItem('streamToken', tokenDetails.token); // StreamService legacy
  localStorage.setItem('user', JSON.stringify(tokenDetails.user)); // Legacy
  
  console.log('[AuthService ✅ FIXED] Tokens saved:', {
    hasUserDetails: !!localStorage.getItem('userDetails'),
    tokenLength: tokenDetails.token?.length || 0,
    expiresIn: Math.round((expireDate - Date.now()) / (1000*60)) + 'min'
  });
}

export function runLogoutTimer(dispatch, timer, navigate) {
  setTimeout(() => {
    dispatch(logoutUser());
  }, timer);
}

export function checkAutoLogin(dispatch, navigate) {
  const tokenDetailsString = localStorage.getItem('userDetails');
  let tokenDetails = '';
  if (!tokenDetailsString) {
    dispatch(logoutUser());
    return;
  }

  tokenDetails = JSON.parse(tokenDetailsString);
  let expireDate = new Date(tokenDetails.expireDate);
  let todaysDate = new Date();

  if (todaysDate > expireDate) {
    dispatch(logoutUser());
    return;
  }

  dispatch(checkAuthStatus());

  const timer = expireDate.getTime() - todaysDate.getTime();
  runLogoutTimer(dispatch, timer, navigate);
}

export function getToken() {
  const tokenDetailsString = localStorage.getItem('userDetails');
  if (!tokenDetailsString) {
    return null;
  }
  
  try {
    const tokenDetails = JSON.parse(tokenDetailsString);
    let expireDate = new Date(tokenDetails.expireDate);
    let todaysDate = new Date();

    if (todaysDate > expireDate) {
      return null;
    }
    
    return tokenDetails.token || null;
  } catch (error) {
    return null;
  }
}

export function getUser() {
  const tokenDetailsString = localStorage.getItem('userDetails');
  if (!tokenDetailsString) {
    return null;
  }
  
  try {
    const tokenDetails = JSON.parse(tokenDetailsString);
    return tokenDetails.user || null;
  } catch (error) {
    return null;
  }
}

export function isEmailVerified() {
  const user = getUser();
  return user ? user.emailVerified : false;
}

export function isPhoneVerified() {
  const user = getUser();
  return user ? user.phoneVerified : false;
}

// Default export for the service
const AuthService = {
  signUp,
  getVerificationConfig,
  sendOTP,
  verifyOTPAndRegister,
  resendOTP,
  sendOTPForLogin,
  verifyOTPAndLogin,
  login,
  googleLogin,
  verifyEmail,
  resendVerification,
  getCurrentUser,
  changePassword,
  formatError,
  saveTokenInLocalStorage,
  runLogoutTimer,
  checkAutoLogin,
  getToken,
  getUser,
  isEmailVerified,
  isPhoneVerified
};

export default AuthService;
